import { computeRetrogradeBands, bandColorsForPlanet } from '../utils/retrogradeBands';

/**
 * Every dynamic trendline, in one colour.
 *
 * <p>Blue because it is not green or red: those belong to the trade decoration, where they mean
 * an outcome. A structural line has no outcome.
 */
export const TRENDLINE_COLOR = '#1565c0';
import { annotateBandCongestion, lightenStroke } from '../utils/bandDensity';
import { abbreviatePlanet } from '../utils/planetAbbreviations';
import { pairDates } from '../utils/cycleBandGating';
import { newOverlayGroupId } from '../utils/drawingStack';

// addAreaHighlightOverlaysUtil.js
// Utility for rendering overlays and updating the Message Panel for area highlight studies, given just date pairs and params.

/**
 * Adds overlays (area highlights, vertical lines, trendlines) for each date pair and updates the Messages Panel.
 * @param {Object} opts
 * @param {Array<{startDate: string, endDate: string}>} opts.datePairs - List of date pairs
 * @param {Array<string>} opts.summaryLines - Lines for the Summary tab
 * @param {Array<string>} opts.futureDates - Lines for the Future Dates tab
 * @param {Object} opts.params - Overlay params (color, label, etc.)
 * @param {Function} opts.addOverlay - Overlay context function
 * @param {Object} opts.barData - { dates, ohlc }
 * @param {Object} [opts.extendedBarData] - { dates, ohlc }
 * @param {Function} [opts.updateMessagesPanel] - Callback to update the Messages Panel
 */
export function addAreaHighlightOverlaysAndMessages({ datePairs, summaryLines, futureDates, params = {}, addOverlay, barData, extendedBarData, updateMessagesPanel, onJournalAppend, isAdmin = false }) {
  /**
   * Snapped start date -> the group id of that period's vertical half.
   *
   * <p>THE JOIN BETWEEN THE TWO HALVES OF THE CROSS. The vertical geometry is drawn per date pair;
   * the horizontal band cannot be, because it runs from one event's start to the NEXT event's
   * start. So the two are produced in different passes and matched afterwards on the date they
   * share, which is the band's own {@code date1}.
   */
  const groupIdByStart = new Map();
  if (!Array.isArray(datePairs) || typeof addOverlay !== 'function') return;

  /**
   * ONE ID FOR THE WHOLE RUN, alongside the per-period `groupId` below.
   *
   * <h2>⚠ WHY A SECOND IDENTITY RATHER THAN REUSING `groupId`</h2>
   *
   * Two features want to group these overlays and they want DIFFERENT groupings, which is why one
   * field could not serve both:
   *
   * <ul>
   *   <li><b>UNDO</b> wants one group per PERIOD. A PTA period is a cross — a vertical time band
   *       and a horizontal price band — and the two halves must disappear together while staying
   *       separable from the next period's cross. That is what `groupId` is for, and the note
   *       below argues it at length.</li>
   *   <li><b>THE HEADER PILL</b> wants one group per RUN. A study is ONE answer however many
   *       intervals it found, and `ActiveOverlayPills`' own docblock says so: *"A fast planet
   *       crossing a sign produces dozens of intervals and they are ONE answer … a pill per
   *       overlay would put twenty-three identical chips in the header for one question."*</li>
   * </ul>
   *
   * <p>⚠ THAT DEDUPE EXISTED AND WAS DEFEATED BY THIS FILE. `activeAreaHighlights` groups by
   * `groupId`, and `groupId` here is `label-start-end` — unique per date pair. So the pill bar was
   * already doing exactly what it was designed to do, once per interval: measured at **34 pills
   * for one Sun-in-Gemini run**, 2991px wide, wrapping to a second row in the header.
   *
   * <p>Making `groupId` per-run instead would have fixed the pills and broken undo back to one
   * press per cross — trading a visible defect for an invisible one. So the run gets its own
   * field and neither consumer has to compromise.
   */
  const runId = newOverlayGroupId(`run-${params.label || params.groupPrefix || 'study'}`);

  /**
   * Every overlay this run emits, stamped.
   *
   * <p>⚠ A WRAPPER RATHER THAN FIVE EDITED CALL SITES. This function emits five kinds —
   * areaHighlight, two vLines, a trendLine and the horizontal bands — and the pill's ✕ must remove
   * ALL of them, because a study that left its vertical markers and trendlines behind would be
   * half-dismissed. Stamping at each call site would be five chances to add a sixth kind later and
   * forget, and the symptom would be orphaned lines nobody can clear.
   */
  const addRunOverlay = (o) => addOverlay({ ...o, runId });

  /** Whether the run's one on-canvas label has been emitted yet — see the band loop below. */
  let labelled = false;
  const overlayData = extendedBarData && extendedBarData.dates && extendedBarData.dates.length > 0 ? extendedBarData : barData;
  if (!overlayData || !Array.isArray(overlayData.dates) || !Array.isArray(overlayData.ohlc)) return;
  
  // THE OTHER SIDE OF THE SAME BOUNDARY. The backend prints the span it answered with; this
  // prints the span that arrived and the span the chart can actually place. If they differ, the
  // loss is in transport or in snapping; if they agree and the chart still stops early, the loss
  // is downstream of here. Either way it stops being a guess.
  {
    const ends = (Array.isArray(datePairs) ? datePairs : [])
      .map(p => (Array.isArray(p) ? p[1] : (p && (p.endDate || p.date2))))
      .filter(Boolean)
      .map(String)
      .sort();
    console.log(`[bands] received ${Array.isArray(datePairs) ? datePairs.length : 0} pair(s) `
      + `spanning ${ends[0] || '-'}..${ends[ends.length - 1] || '-'}; the axis runs `
      + `${overlayData.dates[0]}..${overlayData.dates[overlayData.dates.length - 1]}`);
  }
  // Helper: Snap date to nearest in overlayData.dates
  function snapToNearestDate(date) {
    if (!date) return date;
    if (overlayData.dates.includes(date)) return date;
    const datePart = date.split('T')[0];
    if (overlayData.dates.includes(datePart)) return datePart;
    // Find nearest date
    let minDiff = Infinity, nearest = null;
    for (const d of overlayData.dates) {
      const diff = Math.abs(new Date(d) - new Date(date));
      if (diff < minDiff) { minDiff = diff; nearest = d; }
    }
    if (nearest) {
      console.warn(`[OverlayDebug] Snapping date '${date}' to nearest available date '${nearest}' in overlayData.dates`);
      return nearest;
    }
    console.warn(`[OverlayDebug] Could not snap date '${date}' to any in overlayData.dates`);
    return date;
  }

  // 'lines' draws each window as a single open-to-close line instead of a shaded band. The
  // vertical boundary markers are kept in both styles: they mark where the window starts and ends,
  // which the line alone does not, and they are not the "filled vertical bands" being replaced.
  const linesOnly = params.displayStyle === 'lines';

  // Overlay rendering
  datePairs.forEach((pair, idx) => {
    // One definition of "the two dates of a pair", shared with the cycle buttons. This branch was
    // the only one in the codebase that handled arrays, and being correct here did nothing for the
    // consumer elsewhere that was not.
    let [startDate, endDate] = pairDates(pair);
    if (!startDate || !endDate) return;
    startDate = snapToNearestDate(startDate);
    endDate = snapToNearestDate(endDate);

    // ONE GROUP PER PERIOD, stamped explicitly.
    //
    // The context batches by macrotask, which makes a whole study run one undo unit. That is right
    // for a study and wrong for this: a PTA period is a CROSS - a vertical time band and a
    // horizontal price band - and the two halves are one construct that must appear and disappear
    // together, while remaining separable from the NEXT period's cross.
    //
    // Stamped here rather than relied on from the batcher, because the batcher's unit is "whatever
    // arrived in this tick" and that is a timing property, not a statement about the figure. A
    // study that awaited between pairs would silently split a cross in half.
    const groupId = `${params.groupPrefix || params.label || 'pillar'}-${startDate}-${endDate}`;
    // The HORIZONTAL half is built in a separate pass further down, because a band runs from one
    // event's start to the NEXT event's start and so cannot be produced inside this per-pair loop.
    // Recording the id against the start date is what lets that pass join the two halves back
    // together - see the note there.
    groupIdByStart.set(startDate, groupId);
    const label = params.label || `Strategy Score Study`;
    // Use color from params or default palette inline in overlay objects
    const overlayColor = params.color || defaultColors[idx % defaultColors.length];
    console.log(`[OverlayDebug] Creating overlays for pair: {startDate: ${startDate}, endDate: ${endDate}, color: ${overlayColor}, label: ${label}}`);
    // Area highlight — the shaded band, drawn only in the default display style.
    //
    // 'lines' replaces the band with the connecting line built below. Nothing about the study's
    // report, tables or metrics depends on this: displayStyle reaches only the overlays, and the
    // date pairs it draws from are the same either way.
    if (!linesOnly) {
      /*
       * ⚠ THE LABEL IS ON THE FIRST INTERVAL ONLY — and it is written AFTER `...params`.
       *
       * The renderer draws a markArea's name across the shaded region, so labelling every interval
       * prints the same words once per band. For a sign study over three decades that is 34 copies
       * along the top of the canvas, overlapping into an unreadable smear — measured, before this,
       * at 34 areas all named `GS.Sun in Gemini`.
       *
       * <p>This is the convention `areaHighlightOverlays` already follows for the copilot path, in
       * its own words: *"THE LABEL IS ON THE FIRST BAND ONLY … The pill in the header is what names
       * the whole set now; this is for a reader looking at the chart alone."* One label per run is
       * what a reader needs and is not a smear; it is fixed here rather than in the renderer,
       * because silencing the label there would also silence that deliberate single one.
       *
       * <p>⚠ THE KEY ORDER IS LOAD-BEARING. `params` CARRIES `label` — every caller passes it —
       * so with the spread last, `...params` puts the full label back on every interval and the
       * blanking silently does nothing. It was `label, ...params` before, which is why the local
       * variable was always overridden anyway.
       *
       * <p>⚠ "FIRST EMITTED", NOT `idx === 0`. The loop RETURNS EARLY for a pair with an
       * unresolvable date, so keying on the loop index means a run whose first pair is bad draws
       * no label at all — a study silently unnamed on the canvas, which is the harder failure to
       * notice because it looks like the fix working.
       */
      addRunOverlay({
        type: 'areaHighlight', date1: startDate, date2: endDate, color: overlayColor,
        ...params,
        label: labelled ? '' : label,
        groupId,
      });
      labelled = true;
    }
    // Vertical lines
    // Ensure required fields for verticalLine overlays (planet1, planet2, degrees, etc.)
    const vLineParams = { ...params };
    // Use 'vLine' type (user reports this worked before)
    // Always use black for vLine overlays for maximum visibility
    // The band edges carry no caption. The vLine renderer draws labels rotated 90 degrees, so
    // "Strategy Score Study" ran vertically down every edge of every band -- once per date pair,
    // obscuring the price action the bands exist to highlight. The date went with it: it repeats
    // the axis directly below, and the study is already named in the messages panel.
    addRunOverlay({ ...vLineParams, type: 'vLine', date: startDate, color: 'black', label: '', groupId });
    addRunOverlay({ ...vLineParams, type: 'vLine', date: endDate, color: 'black', label: '', groupId });

    // Trendline - always use snapped dates (same as areaHighlight)
    // Always use extendedBarData for price1/price2 if available
    const trendData = extendedBarData && extendedBarData.dates && extendedBarData.ohlc ? extendedBarData : overlayData;
    const idx1 = trendData.dates.findIndex(d => d === startDate);
    const idx2 = trendData.dates.findIndex(d => d === endDate);
    console.log('[OverlayDebug] trendLine index check', { startDate, endDate, idx1, idx2, datesSample: trendData.dates.slice(Math.max(0, idx1-2), idx1+3), datesLength: trendData.dates.length });
    if (idx1 >= 0 && idx2 >= 0) {
      // Which prices the connecting line joins depends on the display style.
      //
      //   bands  the bar highs, so the line rides above the shaded band rather than through it.
      //   lines  the OPEN of the starting bar to the CLOSE of the ending bar — the move the window
      //          actually captured, which is the figure worth drawing once the band is gone.
      //
      // ohlc is [open, close, low, high]: ECharts candlestick order, not the order OHLC implies.
      const price1 = linesOnly ? trendData.ohlc[idx1][0] : trendData.ohlc[idx1][3];
      const price2 = linesOnly ? trendData.ohlc[idx2][1] : trendData.ohlc[idx2][3];
      if (typeof price1 !== 'number' || typeof price2 !== 'number') {
        console.warn(`[OverlayDebug] TrendLine price1 or price2 is undefined for startDate ${startDate}, endDate ${endDate}: price1=${price1}, price2=${price2}`);
      }
      // Standardize trendLine color: dark green for uptrend, dark red for downtrend, gray for flat
      // BLUE, whichever way the line runs.
      //
      // It was green for an uptrend and red for a downtrend, which put a dynamic trendline in the
      // same two colours the trade decoration uses for WON and LOST. Two unrelated things in one
      // vocabulary: a red trendline and a red losing trade look alike and mean nothing like each
      // other. Direction is already legible from the line's own slope - it is the one thing a line
      // cannot hide - so the colour is free to say "this is structure, not an outcome".
      const trendColor = TRENDLINE_COLOR;
      // Remove color from params to prevent override
      let restParams = params;
      if ('color' in params) {
        // Remove color from params by deleting the property, never defining it as a variable
        restParams = { ...params };
        delete restParams.color;
      }
      addRunOverlay({ ...restParams, groupId, type: 'trendLine', date1: startDate, date2: endDate, price1, price2, color: trendColor });
      console.log('[OverlayDebug] Added trendLine overlay:', { ...restParams, type: 'trendLine', date1: startDate, date2: endDate, price1, price2, color: trendColor });
    } else {
      console.warn(`[OverlayDebug] Could not find indices for trendLine: startDate ${startDate} idx1=${idx1}, endDate ${endDate} idx2=${idx2}`);
      console.log('[OverlayDebug] Available trendData.dates sample:', trendData.dates.slice(0, 5), '...', trendData.dates.slice(-5));
    }
  });
  // ── Horizontal price bands between events ──
  //
  // Drawn for the Planet Retrograde study for everyone, and for EVERY area-highlight study when
  // an admin is viewing. The admin half is deliberately an exploratory tool: this file previously
  // warned against exactly that widening, on the grounds that the range between events means
  // nothing for aspect and ingress studies. That objection still stands for customers, which is
  // why the widening is role-gated rather than general — an admin looking for whether such a
  // range means anything is the person the caveat does not need protecting from.
  //
  // Emitted as `drawRect`, which already renders a markArea bounded on BOTH axes with a fill and
  // a border — that is exactly a horizontal band, so there is no new renderer to write. Bands are
  // added before the per-pair overlays above are drawn on top, so the vertical highlights and
  // trendlines stay readable through them.
  // 'lines' suppresses these too. They are shaded rectangles — the same kind of fill the style
  // exists to remove, just bounded on the price axis instead of only the time axis — so leaving
  // them in made "Lines Only" still draw shading, which is the one thing choosing it asks for.
  // They were not gated before because the style predates them for customers and, for an admin,
  // every area-highlight study draws them: an admin picking Lines Only saw shading regardless.
  const showHorizontalBands = !linesOnly && (Boolean(params.retroType) || Boolean(isAdmin));
  if (showHorizontalBands) {
    const snappedEvents = datePairs
      .map(pair => {
        let sd, ed;
        if (Array.isArray(pair)) { [sd, ed] = pair; }
        else if (pair && typeof pair === 'object') { sd = pair.startDate || pair.date1; ed = pair.endDate || pair.date2; }
        return sd && ed ? { startDate: snapToNearestDate(sd), endDate: snapToNearestDate(ed) } : null;
      })
      .filter(Boolean);

    const rawBands = computeRetrogradeBands(snappedEvents, overlayData.dates, overlayData.ohlc);
    const { fill, stroke } = bandColorsForPlanet(params.planet1);

    // Congestion is measured in bar indices, not dates: dates are strings on a category axis and
    // cannot be compared for overlap. A retrograde study rarely produces overlapping bands, but an
    // aspect study opened up to admins can produce dozens that sit on top of each other.
    const bands = annotateBandCongestion(rawBands.map(b => ({
      ...b,
      x1: overlayData.dates.indexOf(b.date1),
      x2: overlayData.dates.indexOf(b.date2),
    })));

    bands.forEach(band => {
      // ONE UNDO FOR THE WHOLE CROSS. A band and the vertical highlight that opens it are one
      // construct, and undo must take both - it used to take two presses, because the two passes
      // landed in different batches and the context groups by arrival time.
      //
      // A band with no matching start is left UNGROUPED rather than guessed at: the final,
      // open-ended band runs past the last event and belongs to no single period, and inventing a
      // group for it would make one undo delete a band drawn from a different pair's geometry.
      const bandGroupId = groupIdByStart.get(band.date1);
      addRunOverlay({
        ...(bandGroupId ? { groupId: bandGroupId } : {}),
        type: 'drawRect',
        date1: band.date1,
        price1: band.price1,
        date2: band.date2,
        price2: band.price2,
        color: fill,
        // A crowded band gets a dotted, lighter edge so its boundary stays findable among the
        // others without the stack of edges reading as one solid mass.
        borderColor: band.isCongested ? lightenStroke(stroke) : stroke,
        borderType: band.borderType,
        label: `${abbreviatePlanet(params.planet1) || params.label || 'Study'} range${band.isOpenEnded ? ' (now)' : ''}`,
        source: params.retroType ? 'retrogradeBand' : 'studyBand',
        id: `study-band-${params.planet1 || 'study'}-${band.date1}`,
      });
    });
    const congested = bands.filter(b => b.isCongested).length;
    console.log(`[OverlayDebug] Added ${bands.length} horizontal price band(s) for ${params.planet1 || params.label}`
      + ` (${congested} congested, admin=${Boolean(isAdmin)}, retro=${Boolean(params.retroType)})`);
  }

  // Messages Panel
  if (typeof updateMessagesPanel === 'function') {
    const modifiedSummary = [...(summaryLines || [])];
    console.log('[OverlayDebug] Before updateMessagesPanel:', { summaryLines: modifiedSummary, futureDates });
    updateMessagesPanel({ summaryLines: modifiedSummary, futureDates });
    console.log('[OverlayDebug] After updateMessagesPanel:', { summaryLines: modifiedSummary, futureDates });
  }

  // Journal auto-append
  if (typeof onJournalAppend === 'function') {
    onJournalAppend({
      studyLabel: params.label || 'Study',
      summaryLines: summaryLines || [],
      futureDates: futureDates || [],
      datePairCount: Array.isArray(datePairs) ? datePairs.length : 0,
    });
  }
}


// Default color palette
const defaultColors = [
  'rgba(229, 57, 53, 0.55)',    // Red
  'rgba(30, 136, 229, 0.55)',   // Blue
  'rgba(67, 160, 71, 0.55)',    // Green
  'rgba(251, 192, 45, 0.55)',   // Yellow
  'rgba(142, 36, 170, 0.55)',   // Purple
  'rgba(255, 87, 34, 0.55)',    // Orange
  'rgba(120, 144, 156, 0.55)',  // Gray
  'rgba(255, 193, 7, 0.55)',    // Amber
  'rgba(233, 30, 99, 0.55)',    // Pink
  'rgba(0, 188, 212, 0.55)',    // Cyan
  'rgba(205, 220, 57, 0.55)',   // Lime
  'rgba(96, 125, 139, 0.55)',   // Blue Gray
  'rgba(255, 152, 0, 0.55)',    // Deep Orange
  'rgba(124, 179, 66, 0.55)',   // Light Green
  'rgba(255, 255, 255, 0.55)',  // White
  'rgba(0, 0, 0, 0.55)',        // Black
  'rgba(255, 234, 0, 0.55)',    // Bright Yellow
  'rgba(0, 200, 83, 0.55)',     // Vivid Green
  'rgba(213, 0, 249, 0.55)',    // Vivid Purple
  'rgba(255, 61, 0, 0.55)',     // Vivid Red
];
